import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFromUnionFunction,
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionFromUnionSearchFunction,
  SelectOptionSearchFunction,
  TypesenseCollection,
  TypesenseCollectionApiKeyPair,
  TypesenseCollectionName,
  UnionSearchOptions,
} from '../types';
import {
  searchCollection,
  searchCollections,
  TypesenseDocument,
} from '../utils';
import { Project, ProjectOption } from './projects.types';

const BASE_FILTER = 'IsProjectTemplate:false';
const BASE_FILTER_ACTIVE = 'IsProjectTemplate:false && IsActive:true';

const isSearchOptions = (
  o: SearchOptions | UnionSearchOptions,
): o is SearchOptions => !o.filterBy || typeof o.filterBy === 'string';

export const formatProjectDisplayName = (
  name: string,
  projectNumber: string,
) => {
  return projectNumber?.length ? `${name} (${projectNumber})` : name;
};

// Project-specific wrapper for document search
export const getDocumentsPage = async (
  collectionName: TypesenseCollectionName,
  queryByField: string,
  query: string = '*',
  apiKey: string,
  page: number = 1,
  perPage: number = 50,
  filterBy?: string, // e.g., 'IsActive:=true'
  sortBy?: string, // e.g., 'Name:asc' or 'Number:desc'
) => {
  return searchCollection(
    collectionName,
    queryByField,
    query,
    apiKey,
    page,
    perPage,
    filterBy,
    sortBy,
  );
};

export const getSearchProjectSelectOptions: SelectOptionSearchFunction<
  ProjectOption
> = async (
  searchValue: string,
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.PROJECTS_VIEW.name,
    'Name,Number',
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy
      ? `${options.filterBy} && ${BASE_FILTER_ACTIVE}`
      : BASE_FILTER_ACTIVE,
    options.sortBy ? `${options.sortBy}` : 'Name:asc',
  );
  if (!results.success || results.error) {
    throw new Error(results.error || 'Failed to fetch projects from Typesense');
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatProjectDisplayName(item.Name, item.Number),
    id: item.SourceId.toString(),
    currencyId: item.CurrencyId,
    currencyIsoCode: item.CurrencyCode,
    customerId: item.CustomerId,
    legalEntityId: item.LegalEntityId,
    isActive: item.IsActive,
    mainContractId: item.MainContractId,
  }));
  return mappedResults ?? [];
};

export const getSearchProjectSelectOptionsFromUnion: SelectOptionFromUnionSearchFunction<
  ProjectOption
> = async (
  searchValue: string,
  apiKeys: TypesenseCollectionApiKeyPair[],
  pageIndex: number = 1,
  options: SearchOptions | UnionSearchOptions = {},
) => {
  const isFilterByPerCollection = options && !isSearchOptions(options);
  const sortBy = options?.sortBy ?? 'Name:asc';
  const filterByPerCollection = Object.fromEntries(
    apiKeys.map((pair) => {
      const filterBy = isFilterByPerCollection
        ? (options as UnionSearchOptions).filterBy?.[pair.keyName]
        : (options as SearchOptions).filterBy;
      return [
        pair.keyName,
        filterBy ? `${filterBy} && ${BASE_FILTER_ACTIVE}` : BASE_FILTER_ACTIVE,
      ];
    }),
  );
  const results = await searchCollections(
    apiKeys,
    'Name,Number',
    searchValue,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    filterByPerCollection,
    sortBy,
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch projects from Typesense union search',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatProjectDisplayName(item.Name, item.Number),
    id: item.SourceId.toString(),
    currencyId: item.CurrencyId,
    currencyIsoCode: item.CurrencyCode,
    customerId: item.CustomerId,
    legalEntityId: item.LegalEntityId,
    isActive: item.IsActive,
    mainContractId: item.MainContractId,
  }));

  return mappedResults ?? [];
};

/**
 * Retrieves a project select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no project is found with the given ID
 */
export const getProjectSelectOptionById: GetSelectOptionByIdFunction<
  ProjectOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.PROJECTS_VIEW.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && ${BASE_FILTER} && SourceId:=${id}`
      : `${BASE_FILTER} && SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch project by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatProjectDisplayName(item.Name, item.Number),
    id: item.SourceId.toString(),
    currencyId: item.CurrencyId,
    currencyIsoCode: item.CurrencyCode,
    customerId: item.CustomerId,
    legalEntityId: item.LegalEntityId,
    isActive: item.IsActive,
    mainContractId: item.MainContractId,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`Project with id ${id} not found`);
    throw new Error(`Project with id ${id} not found`);
  }

  return mappedResults[0];
};

/**
 * Retrieves a project select option by ID from Typesense union search (can be across multiple collections or same collection with differing API scoped keys).
 * @throws {Error} When the search fails, returns an error, or no project is found with the given ID
 */
export const getProjectSelectOptionByIdFromUnion: GetSelectOptionByIdFromUnionFunction<
  ProjectOption
> = async (
  id: string,
  apiKeys: TypesenseCollectionApiKeyPair[],
  additionalFilterOptions?: SearchOptions | UnionSearchOptions,
) => {
  const isFilterByPerCollection =
    additionalFilterOptions && !isSearchOptions(additionalFilterOptions);
  const filterByPerCollection = Object.fromEntries(
    apiKeys.map((pair) => {
      const filterBy = isFilterByPerCollection
        ? (additionalFilterOptions as UnionSearchOptions).filterBy?.[
            pair.keyName
          ]
        : (additionalFilterOptions as SearchOptions | undefined)?.filterBy;
      return [
        pair.keyName,
        filterBy
          ? `${filterBy} && ${BASE_FILTER} && SourceId:=${id}`
          : `${BASE_FILTER} && SourceId:=${id}`,
      ];
    }),
  );
  const results = await searchCollections(
    apiKeys,
    'Name',
    '*',
    1,
    1,
    filterByPerCollection,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error ||
        'Failed to fetch project by id from Typesense union search',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatProjectDisplayName(item.Name, item.Number),
    id: item.SourceId.toString(),
    currencyId: item.CurrencyId,
    currencyIsoCode: item.CurrencyCode,
    customerId: item.CustomerId,
    legalEntityId: item.LegalEntityId,
    isActive: item.IsActive,
    mainContractId: item.MainContractId,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`Project with id ${id} not found`);
    throw new Error(`Project with id ${id} not found`);
  }

  return mappedResults[0];
};

export const getProjectById = async (
  sourceId: string | null | undefined,
  collectionApiKey: string | null | undefined,
): Promise<Project | null> => {
  if (!sourceId || !collectionApiKey) return null;

  const results = await searchCollection(
    TypesenseCollection.PROJECTS.name,
    'Name',
    '*',
    collectionApiKey,
    1,
    1,
    `SourceId:=${sourceId}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch project by id from Typesense',
    );
  }

  if (!results.documents || results.documents.length === 0) {
    console.debug(`Project with SourceId ${sourceId} not found`);
    return null;
  }

  // Return the raw document with all fields, cast to Project type
  return results.documents[0] as Project;
};
